import { useState } from 'react';
import { Button, Flex } from '@mantine/core';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer';
import { ConfirmationModal } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModal';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

interface Props {
	onSaveClick: () => void;
	onArchive: () => void | Promise<void>;
	onDelete: () => void | Promise<void>;
	isUpdating: boolean;
	isArchiving: boolean;
	isDeleting: boolean;
	archived: boolean;
	documentName: string;
}

export const EditFooter = ({
	onSaveClick,
	onArchive,
	onDelete,
	isUpdating,
	isArchiving,
	isDeleting,
	archived,
	documentName,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);

	const handleConfirmDelete = async () => {
		await onDelete();
		setShowDeleteConfirm(false);
	};

	const handleConfirmArchive = async () => {
		setShowArchiveConfirm(false);
		await onArchive();
	};

	const anyInFlight = isUpdating || isArchiving || isDeleting;

	return (
		<Flex direction="column" gap="0.5rem" mt="1rem">
			<Button
				fullWidth
				type="button"
				loading={isUpdating}
				disabled={anyInFlight || archived}
				onClick={onSaveClick}
			>
				{tMipManage('master_documents.text.save')}
			</Button>

			<Button
				fullWidth
				variant="subtle"
				color="gray"
				c="dark.8"
				loading={isArchiving}
				disabled={anyInFlight}
				onClick={() => setShowArchiveConfirm(true)}
			>
				{tMipManage(
					archived ? 'master_documents.text.unarchive' : 'master_documents.text.archive'
				)}
			</Button>

			<Button
				fullWidth
				variant="subtle"
				color="red"
				loading={isDeleting}
				disabled={anyInFlight}
				onClick={() => setShowDeleteConfirm(true)}
			>
				{tMipManage('master_documents.text.delete')}
			</Button>

			<ConfirmDeleteDrawer
				opened={showDeleteConfirm}
				onClose={() => setShowDeleteConfirm(false)}
				title={tMipManage('master_documents.delete.drawer.title')}
				inputLabel={tMipManage('master_documents.delete.drawer.input_label')}
				inputPlaceholder={tMipManage('master_documents.delete.drawer.input_placeholder')}
				confirmDeleteText={documentName ?? 'DELETE'}
				wordMismatchErrorMessage={tMipManage('master_documents.delete.drawer.mismatch')}
				deleteBtnText={tMipManage('master_documents.delete.drawer.confirm')}
				cancelBtnText={tMipManage('master_documents.delete.drawer.cancel')}
				handleDelete={handleConfirmDelete}
				deleteLoading={isDeleting}
			/>

			{showArchiveConfirm && (
				<ConfirmationModal
					title={tMipManage(
						archived
							? 'master_documents.archive.confirm.unarchive_title'
							: 'master_documents.archive.confirm.archive_title'
					)}
					subtitle={tMipManage(
						archived
							? 'master_documents.archive.confirm.unarchive_subtitle'
							: 'master_documents.archive.confirm.archive_subtitle'
					)}
					confirmationButtonText={tMipManage(
						archived
							? 'master_documents.text.unarchive'
							: 'master_documents.text.archive'
					)}
					onClose={() => setShowArchiveConfirm(false)}
					onSubmit={handleConfirmArchive}
				/>
			)}
		</Flex>
	);
};
